import React, { useContext } from "react";
import classNames from "classnames";
import { parseISO } from "date-fns";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AccountingReportContext } from "../AccountingReport.context";
import { useReportAmountDisplay } from "../useReportAmountDisplay";
import { getDrillDownClassParams } from "../useGLDrillDownUrl";
import { buildGlDrillDownParams, GlDrillDownParams } from "../glDrillDown.lib";
import styles from "./TrialBalanceTable.module.scss";

interface Props {
    value: number;
    accountCode?: string;
    period: ReportPeriodWithLabel;
    firstPeriod: ReportPeriodWithLabel;
    isSummary: boolean;
    onDrillDown?: (params: GlDrillDownParams) => void;
}

export const TrialBalanceViewValueCell: React.FC<Props> = React.memo(
    ({ value, accountCode, period, firstPeriod, isSummary, onDrillDown }) => {
        const { filters } = useContext(AccountingReportContext);
        const drillDownParams = buildGlDrillDownParams({
            startDate: period.startDate,
            endDate: period.endDate,
            entityId: period.entityId ?? filters.entityId,
            accountCodes: accountCode ? [accountCode] : [],
            ledgerId: filters.ledgerId,
            ...getDrillDownClassParams(period, filters),
            pnlBalanceSince: parseISO(firstPeriod.startDate),
            counterpartiesIds: filters.counterpartiesIds,
            withEmptyOrUnverifiedCounterparty:
                filters.withEmptyOrUnverifiedCounterparty,
            hasCounterparty: filters.hasCounterparty,
            accountIds: filters.accountIds,
            subaccountIds: filters.subaccountIds,
        });

        const formatted = useReportAmountDisplay(value);
        const canDrillDown =
            !isSummary &&
            !!value &&
            accountCode !== undefined &&
            onDrillDown !== undefined;

        return (
            <ViewTableCell
                className={classNames(
                    styles.numericCell,
                    formatted.negativeClassName,
                    isSummary && styles.summaryCell,
                )}
            >
                {canDrillDown && onDrillDown ? (
                    <button
                        type="button"
                        className={classNames(
                            "data-table__drill-down",
                            styles.drillDownButton,
                        )}
                        onClick={() => onDrillDown(drillDownParams)}
                    >
                        {formatted.text}
                    </button>
                ) : (
                    formatted.text
                )}
            </ViewTableCell>
        );
    },
);

TrialBalanceViewValueCell.displayName = "TrialBalanceViewValueCell";
